import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, ArrowRight, ShieldCheck, BookOpen, CreditCard, ChevronDown, MapPin } from 'lucide-react';
import PrintButton from '../components/PrintButton';
import SEO from '../components/SEO';
import HelplinesStrip from '../components/HelplinesStrip';
import NationPicker from '../components/NationPicker';
import { IfEnglandWales, IfScotland } from '../i18n/NationContext';
import ConsentCardPocket, { ConsentCardPocketMulti } from '../components/ConsentCardPocket';
import { trackEvent } from '../hooks/useAnonymousTracker';

/**
 * Consent — plain-language UK guide for survivors of sexual violence and
 * domestic abuse. Trauma-informed voice, minimal citations inline, full
 * sources list at the bottom. Deliberately kept shorter than the Process
 * or Rights pages — the point is that a survivor can read it in one sit.
 */

const CONSENT_LOOKS_LIKE = [
  'An enthusiastic, active "yes".',
  'Given freely, without pressure or fear.',
  'From someone who is awake, sober enough, and understanding.',
  'For this specific thing, not something else.',
  'Something either person can take back at any moment.',
];

const CONSENT_IS_NOT = [
  'Silence, freezing, going still or complying to survive.',
  'A "yes" given because you were pressured, threatened or trapped.',
  'From someone asleep, unconscious, or too drunk / drugged to choose.',
  'Consent to one act does not mean consent to another.',
  'The absence of a "no".',
];

const TRAUMA_RESPONSES = [
  { label: 'Fight', body: 'Pushing back, hitting, shouting.' },
  { label: 'Flight', body: 'Trying to get away.' },
  { label: 'Freeze', body: 'Going still, unable to move or speak (tonic immobility).' },
  { label: 'Fawn', body: 'Complying, being polite, going along with it to survive.' },
  { label: 'Flop', body: 'Going limp, dissociating, feeling as if it wasn\u2019t happening to you.' },
];

const SOURCES = [
  {
    label: 'Sexual Offences Act 2003 (England & Wales)',
    url: 'https://www.legislation.gov.uk/ukpga/2003/42/contents',
  },
  {
    label: 'Sexual Offences (Scotland) Act 2009',
    url: 'https://www.legislation.gov.uk/asp/2009/9/contents',
  },
  {
    label: 'Sexual Offences (Northern Ireland) Order 2008',
    url: 'https://www.legislation.gov.uk/nisi/2008/1769/contents',
  },
  {
    label: 'Police, Crime, Sentencing and Courts Act 2022 (positions of trust)',
    url: 'https://www.legislation.gov.uk/ukpga/2022/32/contents/enacted',
  },
  {
    label: 'Serious Crime Act 2015 (grooming, coercive & controlling behaviour)',
    url: 'https://www.legislation.gov.uk/ukpga/2015/9/contents',
  },
  {
    label: 'Domestic Abuse Act 2021',
    url: 'https://www.legislation.gov.uk/ukpga/2021/17/contents/enacted',
  },
  {
    label: 'Modern Slavery Act 2015',
    url: 'https://www.legislation.gov.uk/ukpga/2015/30/contents/enacted',
  },
  {
    label: 'NSPCC — What is grooming?',
    url: 'https://www.nspcc.org.uk/what-is-child-abuse/types-of-abuse/grooming/',
  },
  {
    label: 'CPS legal guidance — Rape and Sexual Offences',
    url: 'https://www.cps.gov.uk/legal-guidance/rape-and-sexual-offences-overview-and-index-2020-updated-guidance',
  },
  {
    label: 'Rape Crisis England & Wales — About consent',
    url: 'https://rapecrisis.org.uk/get-informed/about-sexual-violence/consent/',
  },
  {
    label: 'Rape Crisis Scotland',
    url: 'https://www.rapecrisisscotland.org.uk/',
  },
  {
    label: 'Nexus NI',
    url: 'https://nexusni.org/',
  },
  {
    label: 'NSPCC — Age of consent',
    url: 'https://www.nspcc.org.uk/keeping-children-safe/sex-relationships/sex-law-teenagers/',
  },
];

function List({ items, positive }) {
  const Icon = positive ? CheckCircle2 : XCircle;
  const iconColor = positive ? 'var(--tf-purple-dark)' : '#B85C6F';
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} className="flex flex-col gap-3">
      {items.map((t) => (
        <li key={t} className="flex items-start gap-2.5">
          <Icon
            size={16}
            strokeWidth={2.2}
            color={iconColor}
            style={{ marginTop: 3, flexShrink: 0 }}
            aria-hidden="true"
          />
          <span
            className="font-body"
            style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--tf-text)' }}
          >
            {t}
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function Consent() {
  // URL that the pocket-card QR points to — always the canonical prod URL,
  // tagged so QR-scan arrivals still count under the existing `arrival-qr`
  // stat. Never the emergent preview host.
  const cardUrl = (() => {
    if (typeof window === 'undefined') return 'https://thefullimpact.org/?src=qr';
    try {
      const origin = window.location.origin;
      const host = new URL(origin).hostname;
      if (/preview\.emergentagent\.com$/i.test(host)) {
        return 'https://thefullimpact.org/?src=qr';
      }
      return `${origin}/?src=qr`;
    } catch {
      return 'https://thefullimpact.org/?src=qr';
    }
  })();

  const [cardMenuOpen, setCardMenuOpen] = useState(false);
  const cardMenuRef = useRef(null);
  const cardButtonRef = useRef(null);

  useEffect(() => {
    if (!cardMenuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setCardMenuOpen(false);
        cardButtonRef.current?.focus();
      }
    };
    const onClickAway = (e) => {
      if (
        cardMenuRef.current &&
        !cardMenuRef.current.contains(e.target) &&
        cardButtonRef.current &&
        !cardButtonRef.current.contains(e.target)
      ) {
        setCardMenuOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClickAway);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClickAway);
    };
  }, [cardMenuOpen]);

  const printCard = (bodyClass, eventName) => {
    trackEvent(eventName);
    document.body.classList.add(bodyClass);
    window.print();
    setTimeout(() => document.body.classList.remove(bodyClass), 400);
  };

  const cardOptions = [
    {
      key: 'single',
      label: '1 preview card on A4',
      hint: 'One card, centred on an A4 sheet. For a quick preview or a single hand-out.',
      action: () => printCard('tf-print-consent-card', 'consent-print-pocket-1'),
    },
    {
      key: '10-a4',
      label: '10 pocket cards on A4 (portrait)',
      hint: 'Ten business-card-sized cards, 2 \u00d7 5 grid with faint cut guides.',
      action: () => printCard('tf-print-consent-card-10-a4', 'consent-print-pocket-10-a4'),
    },
  ];

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="consent-screen">
      <SEO
        title="Consent — UK law explained"
        description="A plain-language, trauma-informed UK guide to what consent means in law across England, Wales, Scotland and Northern Ireland. Freezing is not consent."
        path="/app/consent"
      />

      <p className="eyebrow">UK LAW — CONSENT EXPLAINED</p>
      <h1
        className="font-heading mt-3"
        data-testid="consent-title"
        style={{
          fontSize: 'clamp(28px, 4vw, 36px)',
          lineHeight: 1.15,
          color: 'var(--tf-text)',
          letterSpacing: '-0.01em',
        }}
      >
        Consent — what it means, and what it doesn&apos;t.
      </h1>
      <p
        className="font-body mt-4 max-w-xl"
        style={{ fontSize: 14.5, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
      >
        If you&apos;re not sure whether what happened to you counts as rape or sexual assault,
        this page explains the law in plain words. Choose your nation below so we can show
        you the right guidance. Nothing here is legal advice.
      </p>

      <NationPicker testid="consent-nation-picker" />

      <HelplinesStrip variant="sv" />

      {/* Reassurance strip */}
      <section
        className="mt-6 tf-card p-5 sm:p-6"
        data-testid="consent-reassurance"
        style={{
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F4EFF8 100%)',
          borderColor: 'var(--tf-purple-light)',
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, var(--tf-purple) 0%, var(--tf-pink) 100%)',
              color: '#fff',
            }}
          >
            <ShieldCheck size={20} />
          </div>
          <div>
            <p
              className="font-body font-bold"
              style={{
                fontSize: 10.5,
                letterSpacing: '0.22em',
                color: 'var(--tf-purple-dark)',
              }}
            >
              BEFORE YOU READ ON
            </p>
            <h2
              className="font-heading mt-1"
              style={{
                fontSize: 'clamp(19px, 2.4vw, 22px)',
                color: 'var(--tf-text)',
                letterSpacing: '-0.01em',
                lineHeight: 1.25,
              }}
            >
              You are believed. It was not your fault.
            </h2>
            <p
              className="font-body mt-3"
              style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
            >
              Freezing, fawning, going quiet, complying to survive — none of that is
              consent. Reading this page is not a commitment to report anything. It is
              yours to keep, come back to, or share with someone you trust.
            </p>
          </div>
        </div>
      </section>

      <div className="mt-6">
        <PrintButton label="Print / Save this page" />
      </div>

      <IfEnglandWales>

      {/* Section 1 — three words */}
      <section className="mt-12" data-testid="consent-section-definition">
        <p className="eyebrow">SECTION 01</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 24, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          What consent means in law.
        </h2>
        <p
          className="font-body mt-3"
          style={{ fontSize: 14, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          In England &amp; Wales, the law says: <em>&ldquo;A person consents if they agree
          by choice, and have the freedom and capacity to make that choice.&rdquo;</em>{' '}
          Three words matter — <strong>agreement</strong>, <strong>freedom</strong>,{' '}
          <strong>capacity</strong>.
        </p>

        <div className="mt-5 grid sm:grid-cols-3 gap-3">
          {[
            { word: 'AGREEMENT', body: 'A "yes" has to be actively given. Silence isn\u2019t a yes. Not saying "no" isn\u2019t a yes.' },
            { word: 'FREEDOM', body: 'You have to be free to say no without fear. Threats, pressure, coercion, or feeling trapped can all remove freedom.' },
            { word: 'CAPACITY', body: 'You have to be capable of choosing. Asleep, unconscious, very drunk, drugged, or unable to understand — all remove capacity.' },
          ].map((c) => (
            <div key={c.word} className="tf-card p-4" data-testid={`consent-word-${c.word.toLowerCase()}`}>
              <p
                className="font-body font-bold"
                style={{
                  fontSize: 10.5,
                  letterSpacing: '0.22em',
                  color: 'var(--tf-purple-dark)',
                }}
              >
                {c.word}
              </p>
              <p
                className="font-body mt-2"
                style={{ fontSize: 13, color: 'var(--tf-text)', lineHeight: 1.6 }}
              >
                {c.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Section 2 — side by side */}
      <section className="mt-12" data-testid="consent-section-visual">
        <p className="eyebrow">SECTION 02</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 24, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          What consent looks like — and what it doesn&apos;t.
        </h2>
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <div
            className="tf-card p-5"
            data-testid="consent-visual-yes"
            style={{ background: '#FFFFFF', borderColor: 'var(--tf-purple-light)' }}
          >
            <p
              className="font-body font-bold"
              style={{
                fontSize: 10.5,
                letterSpacing: '0.22em',
                color: 'var(--tf-purple-dark)',
              }}
            >
              CONSENT LOOKS LIKE
            </p>
            <div className="mt-3">
              <List items={CONSENT_LOOKS_LIKE} positive />
            </div>
          </div>
          <div
            className="tf-card p-5"
            data-testid="consent-visual-no"
            style={{ background: '#FFFFFF', borderColor: '#F0D6DE' }}
          >
            <p
              className="font-body font-bold"
              style={{
                fontSize: 10.5,
                letterSpacing: '0.22em',
                color: '#B85C6F',
              }}
            >
              CONSENT IS NOT
            </p>
            <div className="mt-3">
              <List items={CONSENT_IS_NOT} positive={false} />
            </div>
          </div>
        </div>

        {/* Print pocket card menu */}
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <div style={{ position: 'relative' }}>
            <button
              ref={cardButtonRef}
              type="button"
              onClick={() => setCardMenuOpen((v) => !v)}
              data-testid="consent-pocket-print-button"
              aria-haspopup="menu"
              aria-expanded={cardMenuOpen}
              aria-label="Print a pocket-size consent card"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-bold tf-focus"
              style={{
                background: '#fff',
                color: 'var(--tf-purple-dark)',
                border: '1px solid var(--tf-purple-light)',
                fontSize: 13,
                minHeight: 40,
              }}
            >
              <CreditCard size={14} strokeWidth={2.2} />
              Print pocket card
              <ChevronDown size={13} strokeWidth={2.2} />
            </button>
            {cardMenuOpen && (
              <div
                ref={cardMenuRef}
                role="menu"
                aria-label="Pocket-card print layouts"
                data-testid="consent-pocket-print-menu"
                className="tf-fade-up"
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  left: 0,
                  zIndex: 60,
                  width: 320,
                  maxWidth: 'calc(100vw - 24px)',
                  background: '#fff',
                  border: '1px solid var(--tf-card-border)',
                  borderRadius: 14,
                  boxShadow: '0 20px 40px rgba(43, 22, 78, 0.14)',
                  padding: 6,
                }}
              >
                {cardOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    role="menuitem"
                    data-testid={`consent-pocket-option-${opt.key}`}
                    onClick={() => {
                      setCardMenuOpen(false);
                      opt.action();
                    }}
                    className="w-full text-left rounded-lg tf-focus"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: '10px 12px',
                      cursor: 'pointer',
                      color: 'var(--tf-text)',
                      display: 'block',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--tf-bg-secondary)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <p
                      className="font-body font-bold"
                      style={{ fontSize: 13, color: 'var(--tf-text)', margin: 0 }}
                    >
                      {opt.label}
                    </p>
                    <p
                      className="font-body"
                      style={{
                        fontSize: 11.5,
                        color: 'var(--tf-text-secondary)',
                        margin: '2px 0 0 0',
                        lineHeight: 1.4,
                      }}
                    >
                      {opt.hint}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
          <p
            className="font-body"
            style={{ fontSize: 12, color: 'var(--tf-text-secondary)', lineHeight: 1.4 }}
          >
            Business-card-sized. QR points to the full Consent guide.
          </p>
        </div>
      </section>

      {/* Section 3 — freeze is not consent */}
      <section className="mt-12" data-testid="consent-section-freeze">
        <p className="eyebrow">SECTION 03</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 24, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          Freezing is not consent.
        </h2>
        <p
          className="font-body mt-3"
          style={{ fontSize: 14, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          Many survivors ask, <em>&ldquo;I didn&apos;t fight or shout — was it really rape?&rdquo;</em>{' '}
          Yes, it can be. UK prosecutors formally recognise five trauma responses. None of
          them is consent.
        </p>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {TRAUMA_RESPONSES.map((r) => (
            <div key={r.label} className="tf-card p-4" data-testid={`consent-5f-${r.label.toLowerCase()}`}>
              <p
                className="font-heading"
                style={{ fontSize: 17, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
              >
                {r.label}
              </p>
              <p
                className="font-body mt-1"
                style={{ fontSize: 13, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
              >
                {r.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Section 4 — age of consent */}
      <section className="mt-12" data-testid="consent-section-age">
        <p className="eyebrow">SECTION 04</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 24, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          Age of consent across the UK.
        </h2>
        <p
          className="font-body mt-3"
          style={{ fontSize: 14, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          The age of consent is <strong>16</strong> across the whole of the UK — England,
          Wales, Scotland and Northern Ireland. It applies equally regardless of gender
          or sexual orientation. Two important exceptions raise it to <strong>18</strong>:
        </p>
        <ul className="mt-4 flex flex-col gap-3" style={{ listStyle: 'none', padding: 0 }}>
          <li className="tf-card p-4">
            <p className="font-body font-bold" style={{ fontSize: 13.5, color: 'var(--tf-text)' }}>
              Positions of trust — age 18
            </p>
            <p
              className="font-body mt-1"
              style={{ fontSize: 13, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
            >
              Teachers, carers, doctors, sports coaches, faith leaders, youth workers —
              anyone with authority or care over you. Extended in 2022 to include sports
              coaches and faith leaders.
            </p>
          </li>
          <li className="tf-card p-4">
            <p className="font-body font-bold" style={{ fontSize: 13.5, color: 'var(--tf-text)' }}>
              Indecent images — age 18
            </p>
            <p
              className="font-body mt-1"
              style={{ fontSize: 13, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
            >
              Any sexual image of someone under 18 is illegal — even a self-taken image,
              even if the person consented at the time.
            </p>
          </li>
          <li
            className="tf-card p-4"
            style={{ background: 'var(--tf-pink-soft)', borderColor: 'var(--tf-pink)' }}
          >
            <p className="font-body font-bold" style={{ fontSize: 13.5, color: 'var(--tf-text)' }}>
              Under 13 — cannot consent, ever
            </p>
            <p
              className="font-body mt-1"
              style={{ fontSize: 13, color: 'var(--tf-text)', lineHeight: 1.6 }}
            >
              The law treats a child under 13 as unable to consent, in every circumstance.
              This is called <em>strict liability</em>.
            </p>
          </li>
        </ul>
      </section>

      {/* Section 5 — When an older adult pursues you just after 16 */}
      <section className="mt-12" data-testid="consent-section-older">
        <p className="eyebrow">SECTION 05</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 24, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          When someone much older pursues you just after 16.
        </h2>
        <p
          className="font-body mt-3"
          style={{ fontSize: 14, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          <em>&ldquo;But she was 16, so it was legal&rdquo;</em> is one of the most common
          things older abusers say &mdash; and one of the most common things survivors
          are told afterwards. The truth is more careful than that, and the law knows it.
        </p>

        <div
          className="mt-5 tf-card p-5"
          data-testid="consent-why-16"
          style={{
            background: '#FFFDFA',
            borderColor: 'var(--tf-purple-light)',
          }}
        >
          <p
            className="font-body font-bold"
            style={{
              fontSize: 10.5,
              letterSpacing: '0.22em',
              color: 'var(--tf-purple-dark)',
            }}
          >
            WHY THE AGE OF CONSENT IS 16
          </p>
          <p
            className="font-body mt-2"
            style={{ fontSize: 13.5, color: 'var(--tf-text)', lineHeight: 1.7 }}
          >
            The UK age of consent was raised to 16 in 1885, after decades of campaigning
            to protect children from adult sexual exploitation. It was set &mdash; and
            remains set &mdash; as a <strong>floor, not a permission slip</strong>. The
            point of the law is to protect young people during a period of continued
            development, while they are still finishing their understanding of adult
            relationships, sex and boundaries.
          </p>
        </div>

        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div
            className="tf-card p-5"
            data-testid="consent-16-does-not"
            style={{ background: '#FFFDFB', borderColor: '#F0D6DE' }}
          >
            <p
              className="font-body font-bold"
              style={{
                fontSize: 10.5,
                letterSpacing: '0.22em',
                color: '#B85C6F',
              }}
            >
              REACHING 16 DOES NOT MEAN
            </p>
            <div className="mt-3">
              <List
                positive={false}
                items={[
                  'Older adults have permission to pursue you.',
                  'The relationship is equal because "you\u2019re both consenting".',
                  'What happened is safe from criminal law just because of a date on the calendar.',
                  'The state has stopped caring about how someone treats you.',
                ]}
              />
            </div>
          </div>
          <div
            className="tf-card p-5"
            data-testid="consent-16-does"
            style={{ background: '#FFFFFF', borderColor: 'var(--tf-purple-light)' }}
          >
            <p
              className="font-body font-bold"
              style={{
                fontSize: 10.5,
                letterSpacing: '0.22em',
                color: 'var(--tf-purple-dark)',
              }}
            >
              REACHING 16 DOES MEAN
            </p>
            <div className="mt-3">
              <List
                positive
                items={[
                  'Age alone can no longer be the basis for a prosecution against your partner.',
                  'Every other element of consent still applies \u2014 agreement, freedom, capacity.',
                  'Grooming, coercive control, exploitation and abuse of trust remain crimes.',
                ]}
              />
            </div>
          </div>
        </div>

        <p className="eyebrow mt-8">WHAT THE LAW CAN STILL DO</p>
        <h3
          className="font-heading mt-2"
          style={{ fontSize: 19, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          Being 16 doesn&apos;t put anyone above the law.
        </h3>
        <ul className="mt-4 flex flex-col gap-3" style={{ listStyle: 'none', padding: 0 }}>
          {[
            {
              title: 'Section 74 — freedom',
              body:
                'If an older adult used their power, money, experience, isolation, or a pattern of grooming to secure a "yes", your freedom to say no may have been removed. Legal consent may not exist, even where you were over 16.',
            },
            {
              title: 'Sexual communication with a child',
              body:
                'Any sexual message from an adult to someone under 16 is a crime (Serious Crime Act 2015, s.67). Messages sent before your 16th birthday remain evidence of a pattern of behaviour.',
            },
            {
              title: 'Meeting a child following grooming',
              body:
                'A crime under the Sexual Offences Act 2003, s.15 (applies to under-16s). Prior grooming behaviour can still be considered as evidence after that age.',
            },
            {
              title: 'Positions of trust — age 18',
              body:
                'Teachers, coaches, faith leaders, carers, doctors, youth workers — the age of consent is 18, regardless of anything else.',
            },
            {
              title: 'Coercive & controlling behaviour',
              body:
                'A crime under the Serious Crime Act 2015 (s.76) and the Domestic Abuse Act 2021. It applies even if you never lived together, and even in a first relationship.',
            },
            {
              title: 'Modern slavery',
              body:
                'If you were given money, drugs, alcohol, gifts or somewhere to escape to in exchange for sex or company, that can be exploitation under the Modern Slavery Act 2015.',
            },
          ].map((item) => (
            <li
              key={item.title}
              className="tf-card p-4"
              data-testid={`consent-law-${item.title.split(' ')[0].toLowerCase()}`}
            >
              <p className="font-body font-bold" style={{ fontSize: 13.5, color: 'var(--tf-text)' }}>
                {item.title}
              </p>
              <p
                className="font-body mt-1"
                style={{ fontSize: 13, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
              >
                {item.body}
              </p>
            </li>
          ))}
        </ul>

        <p className="eyebrow mt-8">WHAT GROOMING LOOKS LIKE</p>
        <h3
          className="font-heading mt-2"
          style={{ fontSize: 19, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          A pattern, not one moment.
        </h3>
        <p
          className="font-body mt-3"
          style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          The signs adults are trained to spot include:
        </p>
        <ul
          className="mt-3 flex flex-col gap-2"
          data-testid="consent-grooming-signs"
          style={{ listStyle: 'none', padding: 0 }}
        >
          {[
            'A big age gap made to feel "mature" or "special".',
            'Gifts, money, alcohol, drugs, or a place to escape to.',
            'Isolation from friends and family.',
            'Secrets \u2014 being told the relationship must stay private.',
            'Love-bombing \u2014 intense affection very early on.',
            'Testing boundaries with jokes, "accidental" touches, or explicit images.',
            'Blaming you for anything sexual, or making you feel you owe them.',
          ].map((t) => (
            <li key={t} className="flex items-start gap-2.5">
              <CheckCircle2
                size={15}
                strokeWidth={2.2}
                color="var(--tf-purple-dark)"
                style={{ marginTop: 3, flexShrink: 0 }}
                aria-hidden="true"
              />
              <span
                className="font-body"
                style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--tf-text)' }}
              >
                {t}
              </span>
            </li>
          ))}
        </ul>

        <div
          className="mt-5 tf-card p-5"
          data-testid="consent-not-your-fault"
          style={{
            background: 'var(--tf-pink-soft)',
            borderColor: 'var(--tf-pink)',
          }}
        >
          <p
            className="font-body"
            style={{
              fontSize: 14,
              color: 'var(--tf-text)',
              lineHeight: 1.65,
            }}
          >
            <strong>If any of that describes what happened to you:</strong> it was not a
            relationship between equals, and it was not your fault. Age is one factor.
            The pattern of behaviour is the bigger one.
          </p>
        </div>

        {/* Under-18 callout */}
        <div
          className="mt-4 tf-card p-5"
          data-testid="consent-under-18-callout"
          style={{
            background: '#FFFFFF',
            borderColor: 'var(--tf-purple-light)',
          }}
        >
          <p
            className="font-body font-bold"
            style={{
              fontSize: 10.5,
              letterSpacing: '0.22em',
              color: 'var(--tf-purple-dark)',
            }}
          >
            IF YOU ARE UNDER 18 NOW
          </p>
          <p
            className="font-body mt-2"
            style={{ fontSize: 13.5, color: 'var(--tf-text)', lineHeight: 1.7 }}
          >
            You can speak to someone in confidence, at any time, for free:
          </p>
          <ul className="mt-3 flex flex-col gap-2" style={{ listStyle: 'none', padding: 0 }}>
            <li className="font-body" style={{ fontSize: 13.5, color: 'var(--tf-text)' }}>
              <strong>Childline</strong> · <a href="tel:08001111" className="tf-link">0800 1111</a> · 24/7 · they won&apos;t tell anyone unless you say so.
            </li>
            <li className="font-body" style={{ fontSize: 13.5, color: 'var(--tf-text)' }}>
              <strong>NSPCC</strong> (for you, or a trusted adult ringing on your behalf) · <a href="tel:08088005000" className="tf-link">0808 800 5000</a>
            </li>
            <li className="font-body" style={{ fontSize: 13.5, color: 'var(--tf-text)' }}>
              <strong>Report abuse online</strong> ·{' '}
              <a
                href="https://www.ceop.police.uk/safety-centre/"
                target="_blank"
                rel="noopener noreferrer"
                className="tf-link"
              >
                CEOP (police)
              </a>
            </li>
          </ul>
        </div>
      </section>

      {/* Section 6 — marriage & stealthing */}
      <section className="mt-12" data-testid="consent-section-marriage">
        <p className="eyebrow">SECTION 06</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 24, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          Marriage, relationships, and condom removal.
        </h2>
        <p
          className="font-body mt-3"
          style={{ fontSize: 14, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          <strong>Being married, in a civil partnership, or in a long-term relationship
          does not give anyone consent to sex.</strong> Marital rape has been a criminal
          offence in the UK since 1991. Every act still needs the same three things —
          agreement, freedom, capacity.
        </p>
        <p
          className="font-body mt-3"
          style={{ fontSize: 14, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          Removing a condom during sex without the other person&apos;s agreement (sometimes
          called <em>&ldquo;stealthing&rdquo;</em>), or agreeing to use one and then not
          doing so, <strong>is not consented sex under UK law</strong>. English and Scottish
          courts have treated it as rape.
        </p>
      </section>

      </IfEnglandWales>

      {/* Scotland — parallel numbered sections, same visual language as E&W */}
      <IfScotland>

      {/* Scope card */}
      <section
        className="mt-8 tf-card p-5 sm:p-6"
        data-testid="consent-scotland-scope"
        style={{
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F4EFF8 100%)',
          borderColor: 'var(--tf-purple-light)',
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, var(--tf-purple) 0%, var(--tf-pink) 100%)',
              color: '#fff',
            }}
            aria-hidden="true"
          >
            <MapPin size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <p
              className="font-body font-bold"
              style={{ fontSize: 10.5, letterSpacing: '0.22em', color: 'var(--tf-purple-dark)' }}
            >
              A COMPLETELY SEPARATE LEGAL SYSTEM
            </p>
            <h2
              className="font-heading mt-1"
              style={{ fontSize: 'clamp(20px, 2.4vw, 23px)', color: 'var(--tf-text)', letterSpacing: '-0.01em', lineHeight: 1.25 }}
            >
              You are reading consent under Scots law.
            </h2>
            <p
              className="font-body mt-3"
              style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
            >
              Scotland is not part of the England &amp; Wales legal system — it has its own
              statute (the <strong>Sexual Offences (Scotland) Act 2009</strong>), its own
              language for consent (<em>&ldquo;free agreement&rdquo;</em>), and its own
              list of situations that remove it. Every section below is Scots law.
            </p>
          </div>
        </div>
      </section>

      {/* Section 1 — free agreement */}
      <section className="mt-12" data-testid="consent-scot-section-definition">
        <p className="eyebrow">SECTION 01 · SCOTS LAW</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 24, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          What consent means in Scots law.
        </h2>
        <p
          className="font-body mt-3"
          style={{ fontSize: 14, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          The Sexual Offences (Scotland) Act 2009 defines consent as{' '}
          <em>&ldquo;free agreement&rdquo;</em> (s.12). Three ideas matter —{' '}
          <strong>agreement</strong>, <strong>freedom</strong>,{' '}
          <strong>capacity</strong>.
        </p>

        <div className="mt-5 grid sm:grid-cols-3 gap-3">
          {[
            { word: 'AGREEMENT', body: 'A "yes" has to be actively given. Silence isn\u2019t a yes. Not saying "no" isn\u2019t a yes.' },
            { word: 'FREEDOM', body: 'You have to be free to say no without fear. Threats, pressure, coercion, unlawful detention or feeling trapped all remove freedom.' },
            { word: 'CAPACITY', body: 'You have to be capable of choosing. Asleep, unconscious, incapacitated by drink or drugs, unable to communicate — all remove capacity.' },
          ].map((c) => (
            <div key={c.word} className="tf-card p-4" data-testid={`consent-scot-word-${c.word.toLowerCase()}`}>
              <p
                className="font-body font-bold"
                style={{ fontSize: 10.5, letterSpacing: '0.22em', color: 'var(--tf-purple-dark)' }}
              >
                {c.word}
              </p>
              <p className="font-body mt-2" style={{ fontSize: 13, color: 'var(--tf-text)', lineHeight: 1.6 }}>
                {c.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Section 2 — s.13 list */}
      <section className="mt-12" data-testid="consent-scot-section-s13">
        <p className="eyebrow">SECTION 02 · SCOTS LAW</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 24, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          Situations that remove &ldquo;free agreement&rdquo;.
        </h2>
        <p
          className="font-body mt-3"
          style={{ fontSize: 14, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          Section 13 of the 2009 Act sets out the specific circumstances in which
          Scots law treats there as being <strong>no consent</strong>. If any of these
          apply, it wasn&apos;t consent — regardless of what the other person believed.
        </p>
        <ul className="mt-5 grid sm:grid-cols-2 gap-3" style={{ listStyle: 'none', padding: 0 }}>
          {[
            ['Force or the threat of force', 'Physical force, or fear of force, against you or someone else.'],
            ['Unlawful detention', 'Being held against your will, formally or informally.'],
            ['Asleep or unconscious', 'Consent cannot be given by someone who is not awake.'],
            ['Incapacitated by drink or drugs', 'Unable to consent because of alcohol, prescription or recreational drugs — including anything spiked.'],
            ['Unable to communicate refusal', 'A physical or mental condition preventing you from refusing.'],
            ['Deception about the act', 'Being deceived about the nature or purpose of what was happening.'],
            ['Mistake about identity', 'The other person impersonated someone known to you (e.g. a partner).'],
            ['Agreement by another person', 'Someone else agreeing on your behalf is not consent.'],
          ].map(([t, b]) => (
            <li key={t} className="tf-card p-4" style={{ borderColor: 'var(--tf-purple-light)' }}>
              <p className="font-body font-bold" style={{ fontSize: 13.5, color: 'var(--tf-text)' }}>
                {t}
              </p>
              <p className="font-body mt-1.5" style={{ fontSize: 13, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}>
                {b}
              </p>
            </li>
          ))}
        </ul>
      </section>

      {/* Section 3 — freeze / silence */}
      <section className="mt-12" data-testid="consent-scot-section-freeze">
        <p className="eyebrow">SECTION 03 · SCOTS LAW</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 24, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          Freeze, fawn or silence is not consent.
        </h2>
        <p
          className="font-body mt-3"
          style={{ fontSize: 14, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          Section 15 of the 2009 Act confirms that consent to conduct does not
          include conduct beyond it. And under s.16, a defendant cannot rely on
          &ldquo;I thought there was consent&rdquo; unless they took{' '}
          <strong>reasonable steps</strong> to check. Freezing, fawning, going
          along with something out of fear, or being too intoxicated to refuse —
          none of these are consent in Scots law.
        </p>
      </section>

      {/* Section 4 — age of consent */}
      <section className="mt-12" data-testid="consent-scot-section-age">
        <p className="eyebrow">SECTION 04 · SCOTS LAW</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 24, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          Age of consent — 16, the same as E&amp;W.
        </h2>
        <p
          className="font-body mt-3"
          style={{ fontSize: 14, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          Under the 2009 Act, the age of consent in Scotland is <strong>16</strong>,
          regardless of sex or gender. Sexual activity with anyone under 13 is
          treated as the most serious category of offence. Between 13 and 15, it
          is still an offence but the specific charge depends on age difference.
        </p>
      </section>

      {/* Section 5 — position of trust */}
      <section className="mt-12" data-testid="consent-scot-section-trust">
        <p className="eyebrow">SECTION 05 · SCOTS LAW</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 24, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          Position of trust — up to age 18.
        </h2>
        <p
          className="font-body mt-3"
          style={{ fontSize: 14, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          Teachers, care workers, faith leaders, sports coaches and anyone in a
          defined position of trust commit an offence if they engage in sexual
          activity with a young person under 18, <strong>even if the young
          person appeared to agree</strong>. The imbalance of power removes true
          free agreement.
        </p>
      </section>

      {/* Section 6 — rape, sexual assault by penetration, stealthing */}
      <section className="mt-12" data-testid="consent-scot-section-rape">
        <p className="eyebrow">SECTION 06 · SCOTS LAW</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 24, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          Rape, sexual assault &amp; stealthing.
        </h2>
        <p
          className="font-body mt-3"
          style={{ fontSize: 14, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          Scots law defines <strong>rape</strong> (s.1) more narrowly than E&amp;W —
          penile penetration only. Non-penile penetration is charged as
          <strong> sexual assault by penetration</strong> (s.2) and carries the same
          maximum sentence: life imprisonment. Removing a condom during sex
          without agreement (&ldquo;stealthing&rdquo;) or agreeing to use one and
          then not doing so <strong>is not consented sex</strong> — Scottish
          courts have treated it as rape.
        </p>
      </section>

      </IfScotland>

      {/* Closing reassurance */}
      <section
        className="mt-12 rounded-2xl p-5"
        data-testid="consent-closing"
        style={{ background: 'var(--tf-bg-secondary)', border: '1px solid var(--tf-card-border)' }}
      >
        <p
          className="font-body text-center"
          style={{ fontSize: 13.5, color: 'var(--tf-text)', lineHeight: 1.7 }}
        >
          Reading the law is not a commitment to report. It is information — yours to keep,
          come back to, or share.<br />
          <strong>Whatever you choose, you are believed.</strong>
        </p>
      </section>

      {/* Next steps */}
      <section className="mt-10" data-testid="consent-next">
        <p className="eyebrow">WHERE TO NEXT</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <Link
            to="/app/rights"
            data-testid="consent-cta-rights"
            className="tf-card tf-card-hover p-4 flex items-center justify-between gap-3"
            style={{ textDecoration: 'none', color: 'var(--tf-text)' }}
          >
            <span className="font-body font-bold" style={{ fontSize: 14 }}>
              Your rights &amp; ISVAs
            </span>
            <ArrowRight size={15} color="var(--tf-purple-dark)" />
          </Link>
          <Link
            to="/app/process"
            data-testid="consent-cta-process"
            className="tf-card tf-card-hover p-4 flex items-center justify-between gap-3"
            style={{ textDecoration: 'none', color: 'var(--tf-text)' }}
          >
            <span className="font-body font-bold" style={{ fontSize: 14 }}>
              What happens if you report (SV)
            </span>
            <ArrowRight size={15} color="var(--tf-purple-dark)" />
          </Link>
          <Link
            to="/app/domestic-abuse"
            data-testid="consent-cta-da"
            className="tf-card tf-card-hover p-4 flex items-center justify-between gap-3"
            style={{ textDecoration: 'none', color: 'var(--tf-text)' }}
          >
            <span className="font-body font-bold" style={{ fontSize: 14 }}>
              Domestic abuse — the calm guide
            </span>
            <ArrowRight size={15} color="var(--tf-purple-dark)" />
          </Link>
          <Link
            to="/app/support"
            data-testid="consent-cta-support"
            className="tf-card tf-card-hover p-4 flex items-center justify-between gap-3"
            style={{ textDecoration: 'none', color: 'var(--tf-text)' }}
          >
            <span className="font-body font-bold" style={{ fontSize: 14 }}>
              Specialist UK helplines
            </span>
            <ArrowRight size={15} color="var(--tf-purple-dark)" />
          </Link>
        </div>
      </section>

      {/* Sources */}
      <section className="mt-12" data-testid="consent-sources">
        <div className="flex items-center gap-2">
          <BookOpen size={15} color="var(--tf-purple-dark)" aria-hidden="true" />
          <p className="eyebrow">SOURCES &amp; FURTHER READING</p>
        </div>
        <p
          className="font-body mt-3"
          style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
        >
          The information above is drawn from the following UK legislation, prosecuting
          guidance and specialist services. Follow any link to check the wording for
          yourself.
        </p>
        <ul className="mt-4 flex flex-col gap-2" style={{ listStyle: 'none', padding: 0 }}>
          {SOURCES.map((s) => (
            <li key={s.url}>
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="tf-link"
                style={{ fontSize: 13, lineHeight: 1.55 }}
              >
                {s.label} →
              </a>
            </li>
          ))}
        </ul>
        <p
          className="font-body mt-5"
          style={{
            fontSize: 11.5,
            color: 'var(--tf-text-muted)',
            lineHeight: 1.55,
          }}
        >
          This page is a plain-language guide, not legal advice. For advice about your
          own situation, please speak to a specialist ISVA, IDVA, Rape Crisis centre or
          solicitor.
        </p>
      </section>

      {/* Off-screen print containers for the pocket card */}
      <ConsentCardPocket url={cardUrl} />
      <ConsentCardPocketMulti url={cardUrl} count={10} layout="10-a4" />
    </div>
  );
}
